(function () {
  // Every level below the first is a push panel — regular submenus and mega submenus share
  // the same markup, so one binder drives both, at any nesting depth.
  const initMobileMegaSubmenu = (header) => {
    const megaSubmenuLinks = header.querySelectorAll(
      "header-mobile-drawer .header-mobile-menu__item--megasubmenu, header-mobile-drawer .header-mobile-menu__item--submenu"
    );
    if (!megaSubmenuLinks || !megaSubmenuLinks.length) return;

    // The panels stop below the drawer's header row so the Close control stays reachable at
    // every level. The row grows with the merchant's logo, so its height is measured rather
    // than assumed.
    const publishDrawerTopHeight = (megaSubmenuEl) => {
      const drawer = megaSubmenuEl.closest(".menu-drawer");
      const topRow = drawer?.querySelector(":scope > .menu-drawer__top");
      if (!drawer || !topRow) return;

      const height = topRow.getBoundingClientRect().height;
      if (!height) return;

      drawer.style.setProperty("--drawer-top-height", `${height}px`);
    };

    const openMobileMegaSubmenu = (megaSubmenuEl) => {
      if (
        !megaSubmenuEl ||
        !megaSubmenuEl.classList.contains("mega-submenu-mobile")
      ) {
        return;
      }

      publishDrawerTopHeight(megaSubmenuEl);
      megaSubmenuEl.inert = false;
      megaSubmenuEl.classList.add("mega-submenu-mobile--visible");
      trapFocus(megaSubmenuEl.querySelector(".mega-submenu-mobile__wrapper"));

      // Without overflow: clip the drawer scrolls to the still off-canvas panel on focus
      const drawer = megaSubmenuEl.closest(".menu-drawer");
      if (drawer && drawer.scrollLeft) drawer.scrollLeft = 0;
    };

    // The trap goes back to the level the panel was opened from — the panel above, or the
    // drawer itself — instead of being dropped, so Tab cannot leave the drawer after a back.
    const closeMobileMegaSubmenu = (megaSubmenuEl, parentLink) => {
      if (
        !megaSubmenuEl ||
        !megaSubmenuEl.classList.contains("mega-submenu-mobile")
      ) {
        return;
      }

      megaSubmenuEl.classList.remove("mega-submenu-mobile--visible");
      // Stays painted while it slides out; inert keeps its rows out of the parent's trap and taps
      megaSubmenuEl.inert = true;

      const parentLevel =
        megaSubmenuEl.parentElement?.closest(".mega-submenu-mobile__wrapper") ||
        megaSubmenuEl.closest(".menu-drawer-container");
      if (parentLevel) {
        trapFocus(parentLevel, parentLink);
      } else {
        removeTrapFocus(parentLink);
      }
    };

    megaSubmenuLinks.forEach((item) => {
      if (item.dataset.megaSubmenuMobileBound) return;
      item.dataset.megaSubmenuMobileBound = "true";

      // Scoped to direct children: a level-1 item also contains its descendants' panels
      const linkEl = item.querySelector(":scope > .header-mobile-menu__link");
      const megaSubmenuEl = item.querySelector(":scope > .mega-submenu-mobile");
      if (!linkEl || !megaSubmenuEl) return;

      const closeButton = megaSubmenuEl.querySelector(
        ":scope > .mega-submenu-mobile__wrapper > .js-mega-submenu-mobile-close-trigger"
      );

      // Where the label is a real link (the top-level setting, a nested parent with a URL, a
      // mega item with a link), the arrow alone opens the panel and the label navigates.
      // Otherwise the whole row opens the panel, the arrow included.
      const arrowTrigger = item.querySelector(
        ":scope > .js-mobile-panel-trigger"
      );
      const openTarget = arrowTrigger || item;

      // A row that is not a link is a span with role="button", which gets no key activation of
      // its own; the keys are forwarded to a click so they take the pointer's path.
      if (linkEl.getAttribute("role") === "button") {
        linkEl.addEventListener("keydown", (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          linkEl.click();
        });
      }

      openTarget.addEventListener("click", (event) => {
        // Only clicks that land inside THIS item's own panel are ignored — a nested row lives
        // inside the panel of the level above.
        if (megaSubmenuEl.contains(event.target)) return;
        event.preventDefault();
        event.stopPropagation();
        openMobileMegaSubmenu(megaSubmenuEl);
        (arrowTrigger || linkEl).setAttribute("aria-expanded", "true");
      });

      closeButton?.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        closeMobileMegaSubmenu(megaSubmenuEl, arrowTrigger || linkEl);
        (arrowTrigger || linkEl).setAttribute("aria-expanded", "false");
      });

      // Closed panels are only visibility-hidden, which the focus trap would still count
      if (!megaSubmenuEl.classList.contains("mega-submenu-mobile--visible")) {
        megaSubmenuEl.inert = true;
      }
      megaSubmenuEl.classList.add("mega-submenu-mobile--initialized");
    });
  };

  const initDesktopSubmenu = (header) => {
    const headerBox = header.querySelector(".header-inner");
    if (!headerBox) return;

    const submenuItems = headerBox.querySelectorAll(
      ".header-inline-menu__item--submenu"
    );
    if (!submenuItems || !submenuItems.length) return;

    // The panel hangs off the bottom edge of the whole bar, not off the item, so the
    // distance between the two is measured; CSS turns it into the panel's transparent
    // top border, which both positions the panel and keeps the pointer inside it.
    const calcSubmenuPosition = () => {
      const headerRect = headerBox.getBoundingClientRect();

      submenuItems.forEach((itemEl) => {
        const itemRect = itemEl.getBoundingClientRect();
        const delta = Math.max(
          Math.ceil(headerRect.bottom - itemRect.bottom),
          0
        );

        itemEl.style.setProperty("--distance-to-menu", `${delta}px`);
      });
    };

    const observer = new ResizeObserver(() => {
      calcSubmenuPosition();
    });

    observer.observe(headerBox);
    header.submenuObserver = observer;

    submenuItems.forEach((itemEl) => {
      if (itemEl.dataset.submenuBound) return;
      itemEl.dataset.submenuBound = "true";

      const panel = itemEl.querySelector(":scope > .header-inline-submenu");
      const list = panel?.querySelector(
        ":scope > .header-inline-submenu__list"
      );

      itemEl.addEventListener("mouseleave", () => {
        const focusElement = document.activeElement;
        if (itemEl.contains(focusElement)) {
          focusElement.blur();
        }
      });

      // Cap each level to what is left below its own top edge, so a long list scrolls
      // instead of running past the viewport. The second level scrolls its LIST, not the
      // panel — the panel is the third level's containing block, and an overflow panel
      // would clip that flyout. Measured on focus as well as hover: the keyboard opens the
      // same panels through :focus-within.
      const capList = () => {
        if (!list) return;
        list.style.setProperty(
          "--clientY",
          `${Math.max(Math.round(list.getBoundingClientRect().top), 0)}px`
        );
      };
      itemEl.addEventListener("mouseenter", capList);
      itemEl.addEventListener("focusin", capList);

      itemEl
        .querySelectorAll(".header-inline-submenu__item--has-children")
        .forEach((parentItem) => {
          const alignNested = () => {
            const nested = parentItem.querySelector(
              ".header-inline-submenu--nested"
            );
            if (!nested) return;
            // The flyout hangs off the panel rather than off its row, so the row's offset
            // inside the panel — minus however far the list has been scrolled — is what
            // places it level with its parent.
            const scrolled = list ? list.scrollTop : 0;
            nested.style.setProperty(
              "--nested-top",
              `${Math.round(parentItem.offsetTop - scrolled)}px`
            );
            nested.style.setProperty(
              "--clientY",
              `${Math.max(Math.round(nested.getBoundingClientRect().top), 0)}px`
            );
          };
          parentItem.addEventListener("mouseenter", alignNested);
          parentItem.addEventListener("focusin", alignNested);
        });
    });
  };

  const initHeaderOverlay = (header) => {
    const main = document.getElementById("MainContent");
    if (!main) return;
    const sections = main.querySelectorAll(".shopify-section");
    const breadcrumbs = document.querySelector("#breadcrumbs");
    const hasOverlayMode = Boolean(
      header.querySelector(".header-wrapper--overlay-mode")
    );
    let isOverlayEnabled = false;

    const removeColorScheme = (element) => {
      if (!element) return;
      let classNames = element.getAttribute("class");
      classNames = classNames.replace(/color-background-\d+/g, "");
      element.setAttribute("class", classNames);
    };

    if (sections.length > 0 && hasOverlayMode) {
      const hasTargetSection =
        sections[0].classList.contains("has-header-overlay");
      const sectionTargetChild = sections[0].querySelector(
        "[data-header-overlay]"
      );
      const headerGroupSections = document.querySelectorAll(
        ".shopify-section-group-header-group"
      );

      if (hasTargetSection && sectionTargetChild) {
        if (headerGroupSections[headerGroupSections.length - 1] === header) {
          isOverlayEnabled = true;
          sections[0].classList.remove("not-margin");
          header.classList.remove("not-mode");
          if (breadcrumbs) {
            removeColorScheme(breadcrumbs);
            const colorScheme = sectionTargetChild.dataset.colorScheme;
            if (colorScheme) breadcrumbs.classList.add(`color-${colorScheme}`);
          }
        } else {
          isOverlayEnabled = false;
          sections[0].classList.add("not-margin");
          header.classList.add("not-mode");
          if (breadcrumbs) removeColorScheme(breadcrumbs);
        }
      } else {
        isOverlayEnabled = false;
        header.classList.add("not-mode");
        if (breadcrumbs) removeColorScheme(breadcrumbs);
      }
    } else {
      isOverlayEnabled = false;
      header.classList.add("not-mode");
      if (breadcrumbs) removeColorScheme(breadcrumbs);
    }
  };

  // The scrim behind an open submenu is driven by the hovered state, so it is wired up
  // whether or not overlay mode is on. Bound once per node: the overlay state above is
  // re-evaluated on every section change, this must not stack listeners with it.
  const controlHoveredState = (header) => {
    const headerWrapper = header.querySelector(".header-wrapper");
    if (!headerWrapper) return;

    const triggeredItems = headerWrapper.querySelectorAll(
      '.header-inline-menu__item[data-show-on-hover="true"]'
    );

    let removeHoverTimeout = null;
    let removeAnimateTimeout = null;

    triggeredItems.forEach((triggeredItem) => {
      if (triggeredItem.dataset.hoverStateBound) return;
      triggeredItem.dataset.hoverStateBound = "true";

      // Focus drives the same state as hover: a keyboard-opened submenu needs the solid bar
      // and the scrim just as much, and it stays until neither is left on any item.
      const onEnter = () => {
        clearTimeout(removeHoverTimeout);
        clearTimeout(removeAnimateTimeout);

        headerWrapper.classList.add("header-hover-triggered");
        headerWrapper.classList.add("header-hover-animate");
      };

      const onLeave = () => {
        clearTimeout(removeHoverTimeout);
        removeHoverTimeout = setTimeout(() => {
          const isStillActive = Array.from(triggeredItems).some(
            (el) => el.matches(":hover") || el.matches(":focus-within")
          );

          if (!isStillActive) {
            headerWrapper.classList.remove("header-hover-triggered");

            removeAnimateTimeout = setTimeout(() => {
              headerWrapper.classList.remove("header-hover-animate");
            }, 300);
          }
        }, 0);
      };

      triggeredItem.addEventListener("mouseenter", onEnter);
      triggeredItem.addEventListener("focusin", onEnter);
      triggeredItem.addEventListener("mouseleave", onLeave);
      triggeredItem.addEventListener("focusout", onLeave);
    });
  };

  // The mega menu panels are siblings of the item list so the modal can grow with them;
  // which one shows is driven from the hovered item.
  const initMegaMenuPanels = () => {
    const modal = document.getElementById("header-mega-menu-modal");
    if (!modal) return;

    const items = modal.querySelectorAll(".mega-menu__item--openable");
    const panels = modal.querySelectorAll(".mega-menu__reveal");
    if (!items.length || !panels.length) return;

    // A pointer heading for the open panel travels diagonally and grazes the rows on the way.
    // A short intent delay lets it pass: the swap only happens if the cursor is still on the
    // new row when the timer fires, and reaching the panel cancels it outright.
    const HOVER_INTENT_DELAY = 120;
    let intentTimeout = null;

    const cancelIntent = () => clearTimeout(intentTimeout);

    const scheduleIntent = (action) => {
      cancelIntent();
      intentTimeout = setTimeout(action, HOVER_INTENT_DELAY);
    };

    const panelEl = modal.querySelector(".mega-menu__panel");

    // The width each open shape gives the panel is declared in CSS, so the shape of the
    // reveal being opened is all this has to publish — the transition between the two
    // definite widths is the browser's. The reveal on its way out is pinned at the width it
    // had and lifted out of the flow, so it fades over the incoming one instead of widening
    // the panel while the two are on screen together.
    let closingPanel = null;

    const settleClosing = () => {
      if (!closingPanel) return;
      closingPanel.classList.remove("mega-menu__reveal--closing");
      closingPanel.removeAttribute("inert");
      closingPanel.style.removeProperty("width");
      closingPanel.hidden = true;
      closingPanel = null;
    };

    // The incoming reveal is held at the width layout gives it while the panel travels, so
    // the panel clips it into view instead of re-laying the card grid out on every frame.
    let pinnedReveal = null;

    const settleWidth = () => {
      panelEl?.style.removeProperty("width");
      if (!pinnedReveal) return;
      pinnedReveal.style.removeProperty("flex");
      pinnedReveal = null;
    };

    if (panelEl && !panelEl.dataset.megaMenuWidthBound) {
      panelEl.dataset.megaMenuWidthBound = "true";
      panelEl.addEventListener("transitionend", (event) => {
        // The pin is only there for the length of the travel
        if (event.target === panelEl && event.propertyName === "width") settleWidth();
      });
    }

    const publishShape = (reveal) => {
      if (!panelEl) return;
      const shape = reveal?.dataset.megaMenuShape;
      if (shape) panelEl.dataset.megaMenuOpen = shape;
      else delete panelEl.dataset.megaMenuOpen;
    };

    const markActive = (index) => {
      items.forEach((item) => {
        item.classList.toggle(
          "mega-menu__item--active",
          index != null && item.dataset.megaMenuItem === index
        );
      });
    };

    // `index` is the item whose reveal to open, or null to close whatever is open.
    const showPanel = (index) => {
      // Anything still mid-flight lands first, so every measurement below reads a settled
      // layout rather than a frame of the animation it interrupted.
      settleClosing();
      settleWidth();

      const target =
        index == null
          ? null
          : Array.prototype.find.call(
              panels,
              (panel) => panel.dataset.megaMenuPanel === index
            ) || null;
      const current =
        Array.prototype.find.call(panels, (panel) => !panel.hidden) || null;

      if (current === target) {
        markActive(index);
        refreshTrap();
        return;
      }

      const from = panelEl ? panelEl.getBoundingClientRect().width : 0;
      // No box below the modal's breakpoint, and none worth animating while the panel is
      // still off screen — the drawer renders these items instead.
      const animate = from > 0 && Boolean(modal.getAttribute("open"));

      if (current && animate) {
        current.style.width = `${current.getBoundingClientRect().width}px`;
        // Still painted, but out of the tab order for the length of the fade — otherwise a
        // shopper tabbing quickly lands inside the ghost of the panel they just left.
        current.setAttribute("inert", "");
        current.classList.add("mega-menu__reveal--closing");
        current.addEventListener(
          "animationend",
          () => {
            if (closingPanel === current) settleClosing();
          },
          { once: true }
        );
        closingPanel = current;
      }

      // Suppressed for the measurement below: the new shape has to land as a finished
      // layout, not as the first frame of the transition it starts.
      if (animate) panelEl.style.transitionProperty = "none";

      panels.forEach((panel) => {
        if (panel === closingPanel) return;
        panel.hidden = panel !== target;
      });

      publishShape(target);
      markActive(index);
      refreshTrap();

      if (!animate) return;

      const to = panelEl.getBoundingClientRect().width;
      const revealWidth = target ? target.getBoundingClientRect().width : 0;

      // Two shapes of the same width swap their content without the panel travelling, so
      // there is nothing to hold the reveal against — and no transitionend to release it.
      if (Math.abs(to - from) < 1) {
        panelEl.style.removeProperty("transition-property");
        return;
      }

      // Held at the width it started from, then handed back to CSS: the panel travels to the
      // width its new shape declares, and the pinned reveal is clipped into view on the way.
      panelEl.style.width = `${from}px`;
      void panelEl.offsetWidth;
      panelEl.style.removeProperty("transition-property");

      if (target) {
        target.style.flex = `0 0 ${revealWidth}px`;
        pinnedReveal = target;
      }

      panelEl.style.removeProperty("width");
    };

    // The focus trap caches its first/last element, and those were resolved while every
    // reveal was still hidden — without this, tabbing off the last item jumps back to the
    // close button instead of entering the panel the item just opened.
    const refreshTrap = () => {
      if (!modal.getAttribute("open")) return;
      const panel = modal.querySelector(".mega-menu__panel");
      if (panel) trapFocus(panel, document.activeElement);
    };

    const resetPanels = () => {
      // Also drops a pending intent: closing the modal must not leave a timer that reopens a
      // panel behind it.
      cancelIntent();
      showPanel(null);
    };

    const itemsRoot = modal.querySelector(".mega-menu__items");

    items.forEach((item) => {
      if (item.dataset.megaMenuItemBound) return;
      item.dataset.megaMenuItemBound = "true";

      const activate = () => showPanel(item.dataset.megaMenuItem);
      item.addEventListener("mouseenter", () => scheduleIntent(activate));
      item.addEventListener("focusin", () => {
        cancelIntent();
        activate();
      });

      // Tab is left alone so it walks the whole item list. Two ways lead across into the
      // panel a row opened: the arrow key from any row, and Tab off the last row — WebKit's
      // sequential navigation skips the reveal entirely and lands on the page behind, and in
      // Chrome this is the step it would take on its own anyway.
      const link = item.querySelector(".mega-menu__item-link");
      link?.addEventListener("keydown", (event) => {
        const isRtl = document.documentElement.getAttribute("dir") === "rtl";
        const stepAcross = event.key === (isRtl ? "ArrowLeft" : "ArrowRight");
        const stepOff =
          event.key === "Tab" &&
          !event.shiftKey &&
          itemsRoot &&
          link === getFocusableElements(itemsRoot).slice(-1)[0];
        if (!stepAcross && !stepOff) return;

        const reveal = modal.querySelector(
          ".mega-menu__reveal:not([hidden]):not(.mega-menu__reveal--closing)"
        );
        if (!reveal) return;
        const first = getFocusableElements(reveal)[0];
        if (!first) return;
        event.preventDefault();
        first.focus();
      });
    });

    // Shift+Tab out of a panel's first control goes back to the row that opened it.
    panels.forEach((panel) => {
      if (panel.dataset.megaMenuPanelBound) return;
      panel.dataset.megaMenuPanelBound = "true";

      panel.addEventListener("mouseenter", cancelIntent);

      panel.addEventListener("keydown", (event) => {
        if (event.key !== "Tab" || !event.shiftKey) return;
        if (event.target !== getFocusableElements(panel)[0]) return;
        const link = modal.querySelector(
          `.mega-menu__item[data-mega-menu-item="${panel.dataset.megaMenuPanel}"] .mega-menu__item-link`
        );
        if (!link) return;
        event.preventDefault();
        link.focus();
      });
    });

    // An item with nothing to reveal closes whatever panel a previous item opened.
    modal
      .querySelectorAll(".mega-menu__item:not(.mega-menu__item--openable)")
      .forEach((item) => {
        if (item.dataset.megaMenuItemBound) return;
        item.dataset.megaMenuItemBound = "true";

        item.addEventListener("mouseenter", () => scheduleIntent(resetPanels));
        item.addEventListener("focusin", () => {
          cancelIntent();
          resetPanels();
        });
      });

    modal.megaMenuShowPanel = showPanel;
    modal.megaMenuResetPanels = resetPanels;
  };

  // The drawer is fixed inside the header section, so the section's sticky transform travels
  // with it: on a scrolled page the drawer opened 56px above the screen and its header row
  // was cut off. The offset is read on the section — the drawer's containing block — never on
  // the drawer itself, whose own slide-in transform would poison the reading.
  const initMobileDrawerAlign = (header) => {
    const drawer = header.querySelector("header-mobile-drawer .menu-drawer");
    const openTrigger = header.querySelector(
      "header-mobile-drawer .js-menu-drawer-open"
    );
    if (!drawer || !openTrigger) return;
    if (openTrigger.dataset.drawerAlignBound) return;
    openTrigger.dataset.drawerAlignBound = "true";

    // iOS Safari resizes the viewport as its toolbar collapses and expands, and 100dvh does
    // not always follow while the page is locked behind the drawer — the panel then runs under
    // the toolbar and its bottom row is cut off. visualViewport reports the visible height
    // exactly, so the drawer takes it while it is open.
    const setDrawerHeight = () => {
      const height = window.visualViewport?.height;
      if (height) drawer.style.setProperty("--drawer-viewport-height", `${height}px`);
    };

    openTrigger.addEventListener("click", () => {
      const { top } = header.getBoundingClientRect();
      drawer.style.setProperty("--drawer-viewport-top", `${-top}px`);
      setDrawerHeight();
    });
    window.visualViewport?.addEventListener("resize", setDrawerHeight);
  };

  const initHeader = () => {
    const header = document.querySelector(".shopify-section-header");
    if (!header) return;

    header.submenuObserver?.disconnect();

    initHeaderOverlay(header);
    controlHoveredState(header);
    initMobileMegaSubmenu(header);
    initMobileDrawerAlign(header);
    initDesktopSubmenu(header);
    initMegaMenuPanels();
  };

  const teardownHeader = () => {
    const header = document.querySelector(".shopify-section-header");
    if (!header) return;
    header.submenuObserver?.disconnect();
  };

  const isHeaderSection = (node) =>
    Boolean(node && node.classList?.contains("shopify-section-header"));

  // A full re-init only when the header section itself re-renders. Any other section
  // change still re-evaluates the overlay state, which depends on what the first
  // section in <main> is; per-node binds above are guarded, so this never stacks
  // listeners.
  const onSectionChange = (event) => {
    if (isHeaderSection(event.target)) {
      initHeader();
      return;
    }

    const header = document.querySelector(".shopify-section-header");
    if (header) initHeaderOverlay(header);
  };

  initHeader();

  document.addEventListener("shopify:section:load", onSectionChange);
  document.addEventListener("shopify:section:reorder", onSectionChange);
  document.addEventListener("shopify:section:unload", (event) => {
    if (isHeaderSection(event.target)) teardownHeader();
  });
})();

// Fixed-position modals inside the header (or the mobile drawer) get their containing
// block from the nearest transformed ancestor, so `top: 0` lands on that box, not the
// viewport. Measured at open time: zero the offsets, read where the wrapper actually is,
// then shift it back by exactly that amount. The drawer insets its box from the screen
// edge, so the horizontal pair matters too — without it the panel kept the side inset but
// none on top and read as cut off.
const alignModalToViewport = (wrapper) => {
  if (!wrapper) return;
  // Cleared first, so the measurement below reads the box as CSS would place it, and so a
  // panel whose box already matches the viewport keeps the plain 100% width — that one still
  // follows a resize while the modal is open.
  wrapper.style.removeProperty("--modal-viewport-top");
  wrapper.style.removeProperty("--modal-viewport-left");
  wrapper.style.removeProperty("--modal-viewport-width");
  // A panel nested in the mobile drawer stays a drill-down of that drawer. The drawer is a
  // transformed, clipping box, so a panel that escapes to the viewport is simply cut off at
  // the drawer's edge from $sm up, where the drawer stops being full width.
  if (wrapper.closest(".menu-drawer")) return;
  const { top, left, width } = wrapper.getBoundingClientRect();
  // The page's visible width — NOT window.innerWidth or the root clientWidth: the theme
  // reserves a stable scrollbar gutter, so those two count 15px the panel must stay out of,
  // and a wrapper that wide ate the panel's right inset.
  const visibleWidth = document.body.clientWidth;
  if (top) wrapper.style.setProperty("--modal-viewport-top", `${-top}px`);
  if (left) wrapper.style.setProperty("--modal-viewport-left", `${-left}px`);
  // Set only when an ancestor's box really narrows the wrapper (the drawer's own inset);
  // otherwise it keeps the plain 100%, which follows a resize on its own.
  if (Math.abs(width - visibleWidth) > 1) {
    wrapper.style.setProperty("--modal-viewport-width", `${visibleWidth}px`);
  }
};

if (!customElements.get("header-search")) {
  customElements.define(
    "header-search",
    class HeaderSearch extends HTMLElement {
      constructor() {
        super();

        this.open = this.onOpen.bind(this);
        this.close = this.onClose.bind(this);
        this.onKeyDownBody = this.onKeydownBody.bind(this);
        this.onKeyDownOption = this.onKeydownOption.bind(this);
        this.onKeyUpOption = this.onKeyupOption.bind(this);
      }

      connectedCallback() {
        this.header = document.querySelector(".header-wrapper");
        this.openBtn = this.querySelector(".js-search-modal-trigger");
        this.modalEl = document.querySelector(".header-search-modal");
        if (!this.modalEl) return;

        this.closeBtn = this.modalEl.querySelector(".js-search-close-trigger");
        this.input = this.modalEl.querySelector("#Search-In-Modal");
        this.overlayEl = this.modalEl.querySelector(
          ".header-search-modal__overlay"
        );
        this.menuEl = this.modalEl.querySelector(
          ".header-search-modal__default"
        );
        this.predictiveSearchEl =
          this.modalEl.querySelector("predictive-search");

        this.isMac = this.identifyDevice();

        this.openBtn?.addEventListener("click", this.open);
        this.closeBtn?.addEventListener("click", this.close);
        this.overlayEl?.addEventListener("click", this.close);

        document.body.addEventListener("keydown", this.onKeyDownBody);
        this.modalEl.addEventListener("keydown", this.onKeyDownOption);
        this.modalEl.addEventListener("keyup", this.onKeyUpOption);
      }

      disconnectedCallback() {
        clearTimeout(this._focusTimeout);
        document.body.removeEventListener("keydown", this.onKeyDownBody);
        this.openBtn?.removeEventListener("click", this.open);
        this.closeBtn?.removeEventListener("click", this.close);
        this.overlayEl?.removeEventListener("click", this.close);
        this.modalEl?.removeEventListener("keydown", this.onKeyDownOption);
        this.modalEl?.removeEventListener("keyup", this.onKeyUpOption);
      }

      onOpen(event) {
        if (event) event.preventDefault();
        if (this.header) this.header.preventHide = true;
        this.isOpen = true;
        alignModalToViewport(this.modalEl);
        this.modalEl.setAttribute("open", true);
        if (this.openBtn) this.openBtn.setAttribute("aria-expanded", "true");

        this.modalEl.addEventListener(
          "transitionend",
          () => {
            if (this.isOpen) {
              trapFocus(this.modalEl);
            }
          },
          { once: true }
        );

        // using setTimeout makes the modal window appear more smoothly
        this._focusTimeout = setTimeout(() => {
          if (this.input) this.input.focus();
        }, 400);

        document.body.classList.add("overflow-hidden-search-open");

        // styles for forced disable body scroll on iOS
        if (typeof window.lockScrollBodyOnIos === "function") {
          this.openScrollY = window.scrollY;
          window.lockScrollBodyOnIos(this.openScrollY);
        }
      }

      onClose(event, elementToFocus = null) {
        if (event) event.preventDefault();
        if (!this.isOpen) return;
        clearTimeout(this._focusTimeout);
        this.isOpen = false;
        this.modalEl.removeAttribute("open");
        if (this.openBtn) this.openBtn.setAttribute("aria-expanded", "false");
        if (this.header) this.header.preventHide = false;

        document.body.classList.remove("overflow-hidden-search-open");

        if (this.input) {
          this.input.setAttribute("aria-activedescendant", "");
        }

        const newFocusElement = elementToFocus ? elementToFocus : this.openBtn;
        removeTrapFocus(newFocusElement);

        // styles for forced disable body scroll on iOS
        if (typeof window.unlockScrollBodyOnIos === "function") {
          window.unlockScrollBodyOnIos(this.openScrollY);
        }
      }

      onKeyupOption(event) {
        const isOpen = this.modalEl.getAttribute("open");
        if (!isOpen || !(this.menuEl || this.predictiveSearchEl)) return;
        if (event.code === "Enter") {
          const target = event.target;

          if (target.tagName.toLowerCase() === "button") {
            return;
          }

          event.preventDefault();
          this.selectOption();
        }
      }

      onKeydownOption(event) {
        const isOpen = this.modalEl.getAttribute("open");
        if (!isOpen || !(this.menuEl || this.predictiveSearchEl)) return;

        if (
          (event.key === "Enter" || event.key === " ") &&
          event.target.matches?.(".predictive-search__skip-to-close")
        ) {
          event.preventDefault();
          this.closeBtn?.focus();
          return;
        }

        if (event.code === "ArrowUp" || event.code === "ArrowDown") {
          const targetTag = event.target.tagName.toLowerCase();
          if (
            targetTag !== "input" &&
            targetTag !== "a" &&
            targetTag !== "div"
          ) {
            return;
          }

          event.preventDefault();
          event.stopImmediatePropagation();
          this.switchOption(event.code === "ArrowUp" ? "up" : "down");
        }
      }

      onKeydownBody(event) {
        const isOpen = this.modalEl.getAttribute("open");

        if (isOpen && event.key === "Escape") {
          this.close();
          return;
        }

        if (!isOpen) {
          const key = event?.key?.toLowerCase();
          const isMac = this.isMac;
          const isCmdK = isMac && event?.metaKey && key === "k";
          const isCtrlK = !isMac && event?.ctrlKey && key === "k";

          if (isCmdK || isCtrlK) {
            event.preventDefault();
            this.open();
          }
        }
      }

      switchOption(direction) {
        if (!this.modalEl.getAttribute("open")) return;

        const moveUp = direction === "up";
        const selectedElement = this.modalEl.querySelector(
          '[aria-selected="true"]'
        );

        const allVisibleElements = Array.from(
          this.modalEl.querySelectorAll("[role='option']")
        ).filter((element) => element.offsetParent !== null);

        if (allVisibleElements.length === 0) return;

        let selectedElementIndex = allVisibleElements.indexOf(selectedElement);
        let activeElementIndex = 0;

        if (selectedElementIndex === -1) {
          activeElementIndex = moveUp ? allVisibleElements.length - 1 : 0;
        } else {
          if (moveUp) {
            activeElementIndex =
              selectedElementIndex === 0
                ? allVisibleElements.length - 1
                : selectedElementIndex - 1;
          } else {
            activeElementIndex =
              selectedElementIndex === allVisibleElements.length - 1
                ? 0
                : selectedElementIndex + 1;
          }
        }

        if (this.predictiveSearchEl && this.predictiveSearchEl.statusElement) {
          this.predictiveSearchEl.statusElement.textContent = "";
        }

        const activeElement = allVisibleElements[activeElementIndex];
        if (!activeElement) return;

        activeElement.setAttribute("aria-selected", true);
        if (selectedElement) {
          selectedElement.setAttribute("aria-selected", false);
        }

        if (this.input) {
          this.input.setAttribute("aria-activedescendant", activeElement.id);
        }

        activeElement.scrollIntoView({
          block: "nearest",
          inline: "nearest",
          behavior: "smooth",
        });
      }

      selectOption() {
        const selectedOption = this.modalEl.querySelector(
          '[aria-selected="true"] a, a[aria-selected="true"]'
        );
        if (selectedOption) selectedOption.click();
      }

      identifyDevice() {
        const isMac =
          /Mac/.test(navigator.platform) ||
          navigator.userAgentData?.platform?.toLowerCase() === "macos" ||
          navigator.userAgentData?.platform?.toLowerCase().includes("mac");

        return isMac;
      }
    }
  );
}

if (!customElements.get("header-mega-menu")) {
  customElements.define(
    "header-mega-menu",
    class HeaderMegaMenu extends HTMLElement {
      constructor() {
        super();

        this.open = this.onOpen.bind(this);
        this.close = this.onClose.bind(this);
        this.onKeyDown = this.onKeydown.bind(this);
        this.onOpened = this.trapWhenVisible.bind(this);
      }

      connectedCallback() {
        this.header = document.querySelector(".header-wrapper");
        this.toggle = this.querySelector(".js-mega-menu-trigger");
        this.modalEl = document.getElementById("header-mega-menu-modal");
        if (!this.modalEl) return;

        this.closeTriggers = this.modalEl.querySelectorAll(
          ".js-mega-menu-close"
        );
        // The overlay carries the same close class but is a div — focus needs the button
        this.closeButton = this.modalEl.querySelector(
          "button.js-mega-menu-close"
        );
        this.panel = this.modalEl.querySelector(".mega-menu__panel");

        this.toggle?.addEventListener("click", this.open);
        this.closeTriggers.forEach((el) =>
          el.addEventListener("click", this.close)
        );
        document.body.addEventListener("keydown", this.onKeyDown);
      }

      disconnectedCallback() {
        this.toggle?.removeEventListener("click", this.open);
        this.closeTriggers?.forEach((el) =>
          el.removeEventListener("click", this.close)
        );
        this.modalEl?.removeEventListener("transitionend", this.onOpened);
        document.body.removeEventListener("keydown", this.onKeyDown);
      }

      onOpen(event) {
        if (event) event.preventDefault();
        if (this.modalEl.getAttribute("open")) {
          this.close();
          return;
        }
        if (this.header) this.header.preventHide = true;
        alignModalToViewport(this.modalEl);
        this.modalEl.setAttribute("open", true);
        this.toggle?.setAttribute("aria-expanded", "true");
        document.body.classList.add("overflow-hidden-mega-menu");

        // The modal is visibility:hidden until its transition runs, and focus() is a no-op
        // on a hidden element — so the trap waits for the panel to actually become visible.
        this.modalEl.addEventListener("transitionend", this.onOpened);
        this.trapWhenVisible();
      }

      trapWhenVisible() {
        if (!this.modalEl.getAttribute("open")) return;
        if (getComputedStyle(this.modalEl).visibility !== "visible") return;

        this.modalEl.removeEventListener("transitionend", this.onOpened);
        if (this.panel) trapFocus(this.panel, this.closeButton || this.panel);
      }

      onClose(event) {
        if (event) event.preventDefault();
        if (!this.modalEl.getAttribute("open")) return;
        this.modalEl.removeEventListener("transitionend", this.onOpened);
        this.modalEl.removeAttribute("open");
        this.toggle?.setAttribute("aria-expanded", "false");
        document.body.classList.remove("overflow-hidden-mega-menu");
        if (this.header) this.header.preventHide = false;
        this.modalEl.megaMenuResetPanels?.();
        removeTrapFocus(this.toggle);
      }

      onKeydown(event) {
        if (event.key === "Escape" && this.modalEl?.getAttribute("open")) {
          this.close();
        }
      }
    }
  );
}

// One dropdown of the region modal. Picking an option only rewrites the hidden inputs of
// every dropdown on the page — the change is submitted by the modal's Save button.
if (!customElements.get("region-select")) {
  customElements.define(
    "region-select",
    class RegionSelect extends HTMLElement {
      constructor() {
        super();

        this.onToggle = this.togglePanel.bind(this);
        this.onDocumentClick = this.handleDocumentClick.bind(this);
        this.onKeyDown = this.handleKeydown.bind(this);
      }

      connectedCallback() {
        this.dropdown = this.querySelector(".drop-down");
        this.field = this.querySelector(".drop-down__field");
        this.list = this.querySelector(".drop-down__list");
        this.valueEl = this.querySelector(".drop-down__value-text");
        this.flagEl = this.querySelector(
          ".drop-down__value .region-select__flag-item"
        );
        this.options = this.querySelectorAll(".drop-down__option");
        this.isLanguage = this.classList.contains("region-select--language");

        this.field?.addEventListener("click", this.onToggle);
        this.options.forEach((option) =>
          option.addEventListener("click", this.onOptionClick.bind(this))
        );
        this.addEventListener("keydown", this.onKeyDown);
        document.addEventListener("click", this.onDocumentClick);
      }

      disconnectedCallback() {
        this.field?.removeEventListener("click", this.onToggle);
        this.removeEventListener("keydown", this.onKeyDown);
        document.removeEventListener("click", this.onDocumentClick);
      }

      get isOpen() {
        return this.field?.getAttribute("aria-expanded") === "true";
      }

      openPanel() {
        this.field?.setAttribute("aria-expanded", "true");
        this.list?.removeAttribute("hidden");
        this.dropdown?.classList.add("drop-down--open");

        const selected = this.querySelector(".drop-down__option--selected");
        if (selected) selected.scrollIntoView({ block: "nearest" });
      }

      closePanel() {
        this.field?.setAttribute("aria-expanded", "false");
        this.list?.setAttribute("hidden", "");
        this.dropdown?.classList.remove("drop-down--open");
      }

      togglePanel() {
        if (this.isOpen) {
          this.closePanel();
        } else {
          this.openPanel();
        }
      }

      handleDocumentClick(event) {
        if (!this.contains(event.target)) this.closePanel();
      }

      handleKeydown(event) {
        if (event.key === "Escape" && this.isOpen) {
          event.stopPropagation();
          this.closePanel();
          this.field?.focus();
          return;
        }

        const navKeys = ["ArrowDown", "ArrowUp", "Home", "End"];
        if (!navKeys.includes(event.key)) return;

        if (!this.isOpen) {
          if (event.key === "Home" || event.key === "End") return;
          event.preventDefault();
          this.openPanel();
          this.focusOption(this.selectedIndex);
          return;
        }

        event.preventDefault();

        const items = Array.from(this.options);
        if (event.key === "Home") return this.focusOption(0);
        if (event.key === "End") return this.focusOption(items.length - 1);

        const step = event.key === "ArrowDown" ? 1 : -1;
        const current = items.indexOf(document.activeElement);
        const next =
          current === -1
            ? this.selectedIndex
            : (current + step + items.length) % items.length;
        this.focusOption(next);
      }

      get selectedIndex() {
        const index = Array.from(this.options).findIndex((option) =>
          option.classList.contains("drop-down__option--selected")
        );
        return index === -1 ? 0 : index;
      }

      focusOption(index) {
        this.options[index]?.focus();
      }

      onOptionClick(event) {
        const option = event.currentTarget;
        const value = option.dataset.value;
        if (!value) return;

        if (this.valueEl && option.dataset.label) {
          this.valueEl.textContent = option.dataset.label;
        }

        if (this.flagEl && option.dataset.flagSrc) {
          this.flagEl.src = option.dataset.flagSrc;
          this.flagEl.removeAttribute("srcset");
        }

        this.options.forEach((el) => {
          el.classList.toggle("drop-down__option--selected", el === option);
          el.setAttribute("aria-selected", el === option ? "true" : "false");
        });

        const inputName = this.isLanguage ? "locale_code" : "country_code";
        document
          .querySelectorAll(`.region-select__form input[name="${inputName}"]`)
          .forEach((input) => {
            input.value = value;
          });

        this.closePanel();
      }
    }
  );
}

if (!customElements.get("modal-region-selector")) {
  customElements.define(
    "modal-region-selector",
    class ModalRegionSelector extends HTMLElement {
      constructor() {
        super();

        this.open = this.onOpen.bind(this);
        this.close = this.onClose.bind(this);
        this.submit = this.onSubmit.bind(this);
        this.onKeyDown = this.handleKeydown.bind(this);
      }

      connectedCallback() {
        this.header = document.querySelector(".header-wrapper");
        this.toggle = this.querySelector(".modal-region-selector__toggle");
        this.modalEl = this.querySelector(".region-modal");
        this.panel = this.querySelector(".region-modal__panel");
        this.closeTriggers = this.querySelectorAll(".js-region-modal-close");
        this.submitButton = this.querySelector(".js-region-modal-submit");

        this.toggle?.addEventListener("click", this.open);
        this.closeTriggers.forEach((el) =>
          el.addEventListener("click", this.close)
        );
        this.submitButton?.addEventListener("click", this.submit);
        document.body.addEventListener("keydown", this.onKeyDown);
      }

      disconnectedCallback() {
        this.toggle?.removeEventListener("click", this.open);
        this.closeTriggers?.forEach((el) =>
          el.removeEventListener("click", this.close)
        );
        this.submitButton?.removeEventListener("click", this.submit);
        document.body.removeEventListener("keydown", this.onKeyDown);
      }

      onOpen(event) {
        if (event) event.preventDefault();
        if (this.getAttribute("open")) {
          this.close();
          return;
        }
        if (this.header) this.header.preventHide = true;
        alignModalToViewport(this.modalEl);
        this.setAttribute("open", "true");
        this.toggle?.setAttribute("aria-expanded", "true");
        document.body.classList.add("overflow-hidden-region-modal");
        if (this.panel) trapFocus(this.panel);
      }

      onClose(event) {
        if (event) event.preventDefault();
        if (!this.getAttribute("open")) return;
        this.removeAttribute("open");
        this.toggle?.setAttribute("aria-expanded", "false");
        document.body.classList.remove("overflow-hidden-region-modal");
        if (this.header) this.header.preventHide = false;
        removeTrapFocus(this.toggle);
      }

      handleKeydown(event) {
        if (event.key === "Escape" && this.getAttribute("open")) {
          this.close();
        }
      }

      onSubmit(event) {
        event.preventDefault();
        const form = this.querySelector(".region-select__form");
        form?.submit();
      }
    }
  );
}
